/**
 * Telling iOS what kind of audio PadLab makes.
 *
 * On iOS every audio context belongs to a session category, and the default one is silenced
 * by the hardware Ring/Silent switch. For a video player that is correct. For an instrument
 * it is not: a soundboard whose pads go quiet because a switch on the side of the phone is
 * flipped reads as a broken app, and the user has no reason to connect the two.
 *
 * `navigator.audioSession` (Safari 16.4+) is the only web API that changes this, so PadLab
 * declares `playback` — "this is the point of the app, play it through the mute switch" —
 * exactly as a native instrument would set `AVAudioSessionCategoryPlayback`.
 *
 * The one exception is recording. `playback` cannot capture, so opening the microphone needs
 * `play-and-record` for as long as the take lasts. That category routes to the receiver on
 * some devices and lowers output level, which is why it is not the default and is handed back
 * the moment the microphone closes.
 *
 * Everything here is feature-detected and failure is silent by design: on a browser without
 * the API the behaviour is exactly what it was before, and there is nothing the user could do
 * about it if we told them.
 */

import { createLogger, tag } from '../core/log';

const log = createLogger('audio-session');

/** The subset of `AudioSessionType` PadLab has a use for. */
export type PadLabAudioSessionType = 'playback' | 'play-and-record';

interface AudioSessionLike {
  type: string;
}

function session(): AudioSessionLike | null {
  if (typeof navigator === 'undefined') return null;
  const candidate = (navigator as Navigator & { audioSession?: AudioSessionLike }).audioSession;
  if (candidate === undefined || typeof candidate !== 'object') return null;
  return candidate;
}

/** True when this browser lets PadLab choose a session category at all. */
export function audioSessionSupported(): boolean {
  return session() !== null;
}

/**
 * Declares how PadLab's audio should behave. Safe to call repeatedly and from anywhere —
 * assigning the same value twice costs nothing and no call throws.
 */
export function setAudioSessionType(type: PadLabAudioSessionType): void {
  const current = session();
  if (current === null) return;
  if (current.type === type) return;
  try {
    current.type = type;
    log.debug('Audio session category set', { type: tag(type) });
  } catch (cause) {
    // A browser that exposes the object but rejects the value leaves us exactly where we
    // started, which is survivable; a thrown error out of an audio path is not.
    log.warn('Audio session category refused', { type: tag(type) });
    void cause;
  }
}
